import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  actNudge,
  createEvent,
  createGoal,
  createRoutine,
  deleteEvent,
  deleteGoal,
  deleteRoutine,
  dismissNudge,
  getAgenda,
  getEventOccurrences,
  getGoals,
  getRoutines,
  logRoutine,
  snoozeNudge,
  updateGoal,
  type Agenda,
  type EventOccurrence,
  type GoalWithProgress,
  type Nudge,
  type Routine,
  type RoutineOccurrence,
} from '../api.js';
import {
  DEFAULT_SCHEDULE,
  describeSchedule,
  fmtGoalValue,
  goalPaceMarker,
  goalPercent,
  goalTone,
  nudgeTone,
  prettyTime,
  relativeDays,
  sortOccurrences,
  toSchedulePayload,
  type ScheduleDraft,
} from '../lib/intent.js';
import { METRICS, metricMeta } from '../metrics.js';
import {
  Button,
  EmptyState,
  Group,
  Icon,
  IconButton,
  IconTile,
  Page,
  Row,
  Segmented,
  Sheet,
  type IconName,
} from '../ui/index.js';

/**
 * Plan — goals, the routines that serve them, the life calendar, and the inbox
 * that ties all three to today.
 *
 * The page leads with what needs him *now*; everything below it is standing
 * context. Forms open in sheets; each row has one primary action and the rest
 * sit behind swipe (touch) or ⋯ (pointer).
 */

type Tab = 'today' | 'goals' | 'calendar';

/** One icon + tile colour per event kind. */
function eventIcon(kind: string): { name: IconName; color: string } {
  switch (kind) {
    case 'vacation':
    case 'travel':
      return { name: 'plane', color: '#0a84ff' };
    case 'birthday':
    case 'anniversary':
      return { name: 'star', color: '#ff375f' };
    case 'appointment':
      return { name: 'clock', color: '#5e5ce6' };
    case 'deadline':
      return { name: 'alert', color: '#ff9500' };
    case 'bill':
    case 'renewal':
      return { name: 'receipt', color: '#34c759' };
    default:
      return { name: 'calendar', color: '#af52de' };
  }
}

const NUDGE_COLOR: Record<ReturnType<typeof nudgeTone>, string> = {
  urgent: '#ff3b30',
  important: '#ff9500',
  normal: '#0a84ff',
  ambient: '#8e8e93',
};

const TONE_PILL: Record<ReturnType<typeof goalTone>, { cls: string; word: string }> = {
  good: { cls: 'hb-pill--good', word: 'On track' },
  warn: { cls: 'hb-pill--warn', word: 'Watch' },
  bad: { cls: 'hb-pill--bad', word: 'Behind' },
  muted: { cls: '', word: 'In progress' },
};

/** A tiny "actions" sheet (⋯ on pointer devices; swipe does the same on touch). */
interface MenuItem {
  label: string;
  icon?: IconName;
  tone?: 'bad';
  onPick: () => void;
}
function ActionSheet({ title, items, onClose }: { title: string; items: MenuItem[]; onClose: () => void }): JSX.Element {
  return (
    <Sheet title={title} onClose={onClose}>
      <ul className="ui-group pl-menu">
        {items.map((it) => (
          <Row
            key={it.label}
            leading={it.icon ? <Icon name={it.icon} size={20} /> : undefined}
            title={it.label}
            tone={it.tone === 'bad' ? 'bad' : 'accent'}
            chevron={false}
            onClick={() => {
              onClose();
              it.onPick();
            }}
          />
        ))}
      </ul>
    </Sheet>
  );
}

export default function Plan(): JSX.Element {
  const [tab, setTab] = useState<Tab>('today');
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  const [goals, setGoals] = useState<GoalWithProgress[]>([]);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [events, setEvents] = useState<EventOccurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<null | 'menu' | 'goal' | 'event'>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [a, g, r, e] = await Promise.all([
        getAgenda(),
        getGoals('active'),
        getRoutines(true),
        getEventOccurrences(),
      ]);
      setAgenda(a);
      setGoals(g);
      setRoutines(r);
      setEvents(e);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openSlots = agenda ? agenda.routines.filter((s) => s.state === 'due' || s.state === 'upcoming').length : 0;
  const nudges = agenda?.nudges.length ?? 0;
  const sub = loading
    ? 'Goals, routines and calendar'
    : nudges + openSlots === 0
      ? 'All caught up'
      : [nudges > 0 ? `${nudges} ${nudges === 1 ? 'thing needs' : 'things need'} you` : null, openSlots > 0 ? `${openSlots} ${openSlots === 1 ? 'routine' : 'routines'} left today` : null]
          .filter(Boolean)
          .join(' · ');

  return (
    <Page
      title="Plan"
      sub={sub}
      className="pl"
      actions={<IconButton icon="plus" label="Add to plan" onClick={() => setAdding('menu')} />}
    >
      <div className="hb-seg">
        <Segmented<Tab>
          label="Plan view"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'today', label: 'Today', count: nudges + openSlots || undefined },
            { value: 'goals', label: 'Goals' },
            { value: 'calendar', label: 'Calendar' },
          ]}
        />
      </div>

      {error && (
        <div className="ui-group pl-error">
          <EmptyState
            icon="alert"
            title="Couldn't load your plan"
            hint={error}
            action={
              <Button variant="secondary" onClick={() => void load()}>
                Retry
              </Button>
            }
          />
        </div>
      )}

      {loading ? (
        <div className="ui-group" aria-hidden="true">
          <div className="hb-skelrow" />
          <div className="hb-skelrow" />
          <div className="hb-skelrow" />
        </div>
      ) : (
        <>
          {tab === 'today' && (
            <TodayTab
              agenda={agenda}
              routines={routines}
              onChange={() => void load()}
              onGoals={() => setTab('goals')}
              onAddGoal={() => setAdding('goal')}
            />
          )}
          {tab === 'goals' && (
            <GoalsTab goals={goals} routines={routines} onChange={() => void load()} onAdd={() => setAdding('goal')} />
          )}
          {tab === 'calendar' && <CalendarTab events={events} onChange={() => void load()} onAdd={() => setAdding('event')} />}
        </>
      )}

      {adding === 'menu' && (
        <Sheet title="Add to plan" onClose={() => setAdding(null)}>
          <ul className="ui-group pl-menu">
            <Row
              leading={<IconTile name="target" color="#30b0c7" />}
              title="Goal"
              sub="A number to move, a habit to keep, a milestone"
              onClick={() => setAdding('goal')}
            />
            <Row
              leading={<IconTile name="calendar" color="#af52de" />}
              title="Event"
              sub="Trips, birthdays, deadlines, renewals"
              onClick={() => setAdding('event')}
            />
          </ul>
          <p className="hb-hint pl-menu__foot">Routines live under a goal — open Goals and tap “Add routine”.</p>
        </Sheet>
      )}
      {adding === 'goal' && (
        <Sheet title="New goal" onClose={() => setAdding(null)}>
          <GoalForm
            onDone={() => {
              setAdding(null);
              setTab('goals');
              void load();
            }}
          />
        </Sheet>
      )}
      {adding === 'event' && (
        <Sheet title="New event" onClose={() => setAdding(null)}>
          <EventForm
            onDone={() => {
              setAdding(null);
              setTab('calendar');
              void load();
            }}
          />
        </Sheet>
      )}
    </Page>
  );
}

// --- Today -----------------------------------------------------------------

function TodayTab({
  agenda,
  routines,
  onChange,
  onGoals,
  onAddGoal,
}: {
  agenda: Agenda | null;
  routines: Routine[];
  onChange: () => void;
  onGoals: () => void;
  onAddGoal: () => void;
}): JSX.Element {
  if (!agenda) {
    return (
      <div className="ui-group">
        <EmptyState icon="calendar" title="Nothing to show yet" />
      </div>
    );
  }

  const slots = sortOccurrences(agenda.routines);
  const open = slots.filter((s) => s.state === 'due' || s.state === 'upcoming');
  const settled = slots.filter((s) => s.state !== 'due' && s.state !== 'upcoming');
  const quiet = agenda.nudges.length === 0 && open.length === 0;

  return (
    <div className="pl-stack">
      {quiet && (routines.length > 0 || agenda.goals.length > 0) && (
        <div className="ui-group pl-clear">
          <EmptyState icon="check" title="Nothing needs you right now" hint="Reminders, goals drifting off pace and events worth preparing for land here." />
        </div>
      )}

      <Group header="Needs you" id="pl-needs">
        {agenda.nudges.map((n) => (
          <NudgeRow key={n.id} nudge={n} onChange={onChange} />
        ))}
      </Group>

      <Group header="Today's routines" id="pl-routines">
        {[...open, ...settled].map((s) => (
          <SlotRow key={`${s.routineId}:${s.dueAt}`} slot={s} onChange={onChange} />
        ))}
      </Group>

      <Group header="Coming up" id="pl-coming">
        {agenda.events.map((e) => {
          const ic = eventIcon(e.kind);
          return (
            <Row
              key={`${e.id}:${e.startsAt}`}
              leading={<IconTile name={ic.name} color={ic.color} />}
              title={e.title}
              sub={e.detail ?? e.location ?? undefined}
              detail={relativeDays(e.daysUntil)}
            />
          );
        })}
      </Group>

      <Group header="Where you stand" id="pl-stand" action={agenda.goals.length > 0 ? { label: 'Goals', onClick: onGoals } : undefined}>
        {agenda.goals.map((g) => {
          const tone = goalTone(g);
          return (
            <Row
              key={g.goal.id}
              leading={<span className={`pl-tonedot pl-tonedot--${tone}`} aria-hidden="true" />}
              title={g.goal.title}
              sub={g.summary}
              onClick={onGoals}
            />
          );
        })}
      </Group>

      {routines.length === 0 && agenda.goals.length === 0 && <EmptyStart onAdd={onAddGoal} />}
    </div>
  );
}

/** First-run guidance, shown only when the user has neither goals nor routines. */
function EmptyStart({ onAdd }: { onAdd: () => void }): JSX.Element {
  return (
    <div className="ui-group">
      <EmptyState
        icon="target"
        title="Nothing set up yet"
        hint="Start with a goal — a number to move, or a habit to keep. Attach a routine and it will remind you and track whether you did it."
        action={
          <Button icon="plus" onClick={onAdd}>
            New goal
          </Button>
        }
      />
    </div>
  );
}

function NudgeRow({ nudge, onChange }: { nudge: Nudge; onChange: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);

  /** Run a mutation, then let the parent refetch. Errors surface as an alert. */
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      onChange();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  // A routine nudge carries the slot it belongs to, so "Done" can settle the
  // occurrence itself rather than merely acknowledging the message.
  const routineAction =
    nudge.action?.kind === 'log_routine' && nudge.action.routineId
      ? { routineId: nudge.action.routineId, dueAt: nudge.action.dueAt }
      : null;

  const primary = routineAction
    ? {
        label: 'Done',
        run: () =>
          act(() =>
            logRoutine(routineAction.routineId, {
              dueAt: routineAction.dueAt,
              status: 'done',
            }),
          ),
      }
    : { label: 'Got it', run: () => act(() => actNudge(nudge.id)) };
  const later = (): void => void act(() => snoozeNudge(nudge.id, 120));
  const dismiss = (): void => void act(() => dismissNudge(nudge.id));

  return (
    <>
      <Row
        className={`pl-nudge pl-nudge--${nudgeTone(nudge)}`}
        leading={<IconTile name="bell" color={NUDGE_COLOR[nudgeTone(nudge)]} />}
        title={nudge.title}
        sub={nudgeBody(nudge.title, nudge.body)}
        swipe={{
          leading: { label: primary.label, icon: 'check', tone: 'good', onAction: () => void primary.run() },
          trailing: [
            { label: 'Later', icon: 'clock', tone: 'warn', onAction: later },
            { label: 'Dismiss', icon: 'x', tone: 'bad', onAction: dismiss },
          ],
        }}
        trailing={
          <>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void primary.run()}>
              {primary.label}
            </Button>
            <IconButton icon="more" label="More actions" onClick={() => setMenu(true)} className="pl-more" />
          </>
        }
      />
      {menu && (
        <ActionSheet
          title={nudge.title}
          onClose={() => setMenu(false)}
          items={[
            { label: 'Remind me in 2 hours', icon: 'clock', onPick: later },
            { label: 'Dismiss', icon: 'x', tone: 'bad', onPick: dismiss },
          ]}
        />
      )}
    </>
  );
}

/** A nudge's body minus a repeat of its title ("X renews" / "X renews due Fri" → "Due Fri"). */
function nudgeBody(title: string, body: string | null | undefined): string | undefined {
  const b = body?.trim() ?? '';
  const t = title.trim();
  // A body that only names the kind ("event") says nothing.
  if (!b || /^(event|appointment|birthday|deadline)$/i.test(b)) return undefined;
  if (t && b.toLowerCase().startsWith(t.toLowerCase()) && b.length > t.length) {
    const rest = b.slice(t.length).replace(/^[\s:·—–-]+/, '');
    if (rest) return rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  return b;
}

const SLOT_WORD: Record<RoutineOccurrence['state'], string> = {
  due: 'Due now',
  upcoming: 'Later today',
  done: 'Done',
  skipped: 'Skipped',
  missed: 'Missed',
} as Record<RoutineOccurrence['state'], string>;

function SlotRow({ slot, onChange }: { slot: RoutineOccurrence; onChange: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);

  const settle = async (status: 'done' | 'skipped') => {
    setBusy(true);
    try {
      await logRoutine(slot.routineId, { dueAt: slot.dueAt, status });
      onChange();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not log');
    } finally {
      setBusy(false);
    }
  };

  const settled = slot.state === 'done' || slot.state === 'skipped' || slot.state === 'missed';

  return (
    <Row
      className={`pl-slot pl-slot--${slot.state}`}
      tone={settled ? 'muted' : 'default'}
      leading={<span className="pl-time num">{prettyTime(slot.localTime)}</span>}
      title={slot.routineTitle}
      sub={
        <span className={`pl-slot__state pl-slot__state--${slot.state}`}>
          {slot.state === 'done' && <Icon name="check" size={14} />}
          {SLOT_WORD[slot.state] ?? slot.state}
        </span>
      }
      swipe={
        settled
          ? undefined
          : {
              leading: { label: 'Done', icon: 'check', tone: 'good', onAction: () => void settle('done') },
              trailing: [{ label: 'Skip', tone: 'neutral', onAction: () => void settle('skipped') }],
            }
      }
      trailing={
        settled ? undefined : (
          <>
            <Button variant="plain" size="sm" disabled={busy} onClick={() => void settle('skipped')}>
              Skip
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void settle('done')}>
              Done
            </Button>
          </>
        )
      }
    />
  );
}

// --- Goals -----------------------------------------------------------------

function GoalsTab({
  goals,
  routines,
  onChange,
  onAdd,
}: {
  goals: GoalWithProgress[];
  routines: Routine[];
  onChange: () => void;
  onAdd: () => void;
}): JSX.Element {
  if (goals.length === 0) {
    return (
      <div className="ui-group">
        <EmptyState
          icon="target"
          title="No active goals"
          hint="A goal can be a number to move (weight to 180 lb by Sept 11), a habit to keep (minoxidil twice a day), or just something you're working on."
          action={
            <Button icon="plus" onClick={onAdd}>
              New goal
            </Button>
          }
        />
      </div>
    );
  }
  return (
    <div className="pl-stack">
      {goals.map((g) => (
        <GoalCard
          key={g.goal.id}
          entry={g}
          routines={routines.filter((r) => r.goalId === g.goal.id)}
          onChange={onChange}
        />
      ))}
      <Button variant="secondary" icon="plus" block onClick={onAdd}>
        New goal
      </Button>
    </div>
  );
}

function GoalCard({
  entry,
  routines,
  onChange,
}: {
  entry: GoalWithProgress;
  routines: Routine[];
  onChange: () => void;
}): JSX.Element {
  const [addingRoutine, setAddingRoutine] = useState(false);
  const [menu, setMenu] = useState(false);
  const pct = goalPercent(entry);
  const pace = goalPaceMarker(entry);
  const tone = goalTone(entry);
  const pill = TONE_PILL[tone];

  const achieved = (): void => {
    void updateGoal(entry.goal.id, { status: 'achieved' }).then(onChange);
  };
  const remove = (): void => {
    if (window.confirm(`Delete "${entry.goal.title}"? Its routines are kept.`)) {
      void deleteGoal(entry.goal.id).then(onChange);
    }
  };
  const removeRoutine = (r: Routine): void => {
    if (window.confirm(`Delete routine "${r.title}" and its history?`)) {
      void deleteRoutine(r.id).then(onChange);
    }
  };

  return (
    <section className={`ui-groupwrap pl-goal pl-goal--${tone}`} aria-label={entry.goal.title}>
      <div className="ui-group__head">
        <h2 className="ui-group__title pl-goal__title">{entry.goal.title}</h2>
        <IconButton icon="more" label={`Actions for ${entry.goal.title}`} onClick={() => setMenu(true)} className="pl-goal__more" />
      </div>
      <ul className="ui-group">
        <li className="ui-swipe pl-goal__progress">
          <div className="pl-goal__top">
            <span className={`hb-pill ${pill.cls}`}>{pill.word}</span>
            {pct != null && <span className="pl-goal__pct num">{pct}%</span>}
          </div>
          <p className="pl-goal__summary">{entry.summary}</p>
          {pct != null && (
            <div className="pl-goalbar">
              <div className="hb-bar__track">
                <div className={`hb-bar__fill pl-goalbar__fill--${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              {/* Where the plan says you should be today — the honest comparison. */}
              {pace != null && <div className="pl-goalbar__pace" style={{ left: `${pace}%` }} title="On-pace target for today" />}
              {entry.metric && (
                <div className="pl-goalbar__legend num">
                  <span>{fmtGoalValue(entry.metric.currentValue, entry.goal.unit)}</span>
                  <span>→ {fmtGoalValue(entry.goal.targetValue, entry.goal.unit)}</span>
                </div>
              )}
            </div>
          )}
          {entry.metric && entry.metric.projectedDate && (
            <p className="pl-goal__note">
              At your current rate you'd get there {entry.metric.projectedDate}
              {entry.goal.targetDate ? ` (target ${entry.goal.targetDate})` : ''}.
            </p>
          )}
        </li>
        {routines.map((r) => (
          <Row
            key={r.id}
            leading={<IconTile name="repeat" color="#30b0c7" />}
            title={r.title}
            sub={describeSchedule(r.schedule)}
            swipe={{ trailing: [{ label: 'Delete', icon: 'trash', tone: 'bad', onAction: () => removeRoutine(r) }] }}
            trailing={<IconButton icon="trash" label={`Delete routine ${r.title}`} onClick={() => removeRoutine(r)} className="pl-del" />}
          />
        ))}
        <li className="ui-swipe">
          <button type="button" className="ui-more pl-addroutine" onClick={() => setAddingRoutine(true)}>
            <Icon name="plus" size={18} /> Add routine
          </button>
        </li>
      </ul>
      {entry.goal.motivation && <p className="ui-group__foot pl-why">“{entry.goal.motivation}”</p>}

      {addingRoutine && (
        <Sheet title={`Routine for ${entry.goal.title}`} onClose={() => setAddingRoutine(false)}>
          <RoutineForm
            goalId={entry.goal.id}
            onDone={() => {
              setAddingRoutine(false);
              onChange();
            }}
          />
        </Sheet>
      )}
      {menu && (
        <ActionSheet
          title={entry.goal.title}
          onClose={() => setMenu(false)}
          items={[
            { label: 'Mark achieved', icon: 'check', onPick: achieved },
            { label: 'Add routine', icon: 'repeat', onPick: () => setAddingRoutine(true) },
            { label: 'Delete goal', icon: 'trash', tone: 'bad', onPick: remove },
          ]}
        />
      )}
    </section>
  );
}

function GoalForm({ onDone }: { onDone: () => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<'metric' | 'habit' | 'milestone' | 'open'>('open');
  const [metricType, setMetricType] = useState('weight_kg');
  const [targetValue, setTargetValue] = useState('');
  const [direction, setDirection] = useState<'down' | 'up' | 'maintain'>('down');
  const [unit, setUnit] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [motivation, setMotivation] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await createGoal({
        title: title.trim(),
        kind,
        motivation: motivation.trim() || null,
        targetDate: targetDate || null,
        ...(kind === 'metric'
          ? {
              metricType,
              targetValue: Number(targetValue),
              direction,
              unit: unit.trim() || null,
            }
          : {}),
      });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not create goal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hb-form pl-form" onSubmit={submit}>
      <div className="pl-grid">
        <label className="hb-field pl-wide">
          <span>What are you working toward?</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>

        <label className="hb-field">
          <span>Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="open">Open-ended</option>
            <option value="metric">Move a number</option>
            <option value="habit">Keep a habit</option>
            <option value="milestone">One-off milestone</option>
          </select>
        </label>

        <label className="hb-field">
          <span>Target date</span>
          <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
        </label>

        {kind === 'metric' && (
          <>
            <label className="hb-field">
              <span>Metric</span>
              <select
                value={metricType}
                onChange={(e) => {
                  setMetricType(e.target.value);
                  // Pre-fill the unit from the catalog so the goal reads correctly
                  // without the user having to know the canonical unit.
                  if (!unit.trim()) setUnit(metricMeta(e.target.value).unit);
                }}
              >
                {METRICS.map((m) => (
                  <option key={m.type} value={m.type}>
                    {m.label}
                    {m.unit ? ` (${m.unit})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="hb-field">
              <span>Target value</span>
              <input
                type="number"
                step="any"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                required
              />
            </label>
            <label className="hb-field">
              <span>Direction</span>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value as typeof direction)}
              >
                <option value="down">Lower is better</option>
                <option value="up">Higher is better</option>
                <option value="maintain">Hold steady</option>
              </select>
            </label>
            <label className="hb-field">
              <span>Unit (optional)</span>
              <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="kg" />
            </label>
          </>
        )}

        <label className="hb-field pl-wide">
          <span>Why it matters (the coach reads this)</span>
          <input
            value={motivation}
            onChange={(e) => setMotivation(e.target.value)}
            placeholder="So I feel better on the bike"
          />
        </label>
      </div>

      {err && <div className="hb-form__error">{err}</div>}
      <div className="pl-actions">
        <button className="ui-btn ui-btn--primary ui-btn--lg ui-btn--block" type="submit" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : 'Create goal'}
        </button>
      </div>
    </form>
  );
}

function RoutineForm({ goalId, onDone }: { goalId: string; onDone: () => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const [draft, setDraft] = useState<ScheduleDraft>({ ...DEFAULT_SCHEDULE });
  const [detail, setDetail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const setTimeAt = (i: number, value: string) => {
    setDraft((d) => ({ ...d, times: d.times.map((t, j) => (j === i ? value : t)) }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await createRoutine({
        title: title.trim(),
        detail: detail.trim() || null,
        goalId,
        schedule: toSchedulePayload(draft),
        timezone: tz,
      });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not create routine');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hb-form pl-form" onSubmit={submit}>
      <div className="pl-grid">
        <label className="hb-field pl-wide">
          <span>Routine</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Minoxidil"
            required
            autoFocus
          />
        </label>

        <label className="hb-field">
          <span>Cadence</span>
          <select
            value={draft.type}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value as ScheduleDraft['type'] }))}
          >
            <option value="times_per_day">Every day</option>
            <option value="days_per_week">Certain weekdays</option>
            <option value="interval_days">Every N days</option>
          </select>
        </label>

        {draft.type === 'interval_days' && (
          <label className="hb-field">
            <span>Every N days</span>
            <input
              type="number"
              min={1}
              max={365}
              value={draft.every}
              onChange={(e) => setDraft((d) => ({ ...d, every: Number(e.target.value) }))}
            />
          </label>
        )}

        {draft.type === 'days_per_week' && (
          <div className="hb-field pl-wide">
            <span>Days</span>
            <div className="daypick">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((name, i) => (
                <button
                  key={name}
                  type="button"
                  className={`ui-chip${draft.days.includes(i) ? ' ui-chip--on' : ''}`}
                  aria-pressed={draft.days.includes(i)}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      days: d.days.includes(i)
                        ? d.days.filter((x) => x !== i)
                        : [...d.days, i].sort(),
                    }))
                  }
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="hb-field pl-wide">
          <span>Times</span>
          <div className="timepick">
            {draft.times.map((t, i) => (
              <span key={i} className="timepick__row">
                <input type="time" value={t} onChange={(e) => setTimeAt(i, e.target.value)} />
                {draft.times.length > 1 && (
                  <button
                    type="button"
                    className="ui-btn ui-btn--secondary ui-btn--sm"
                    onClick={() =>
                      setDraft((d) => ({ ...d, times: d.times.filter((_, j) => j !== i) }))
                    }
                    aria-label="Remove time"
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
            <button
              type="button"
              className="ui-btn ui-btn--secondary ui-btn--sm"
              onClick={() => setDraft((d) => ({ ...d, times: [...d.times, '12:00'] }))}
            >
              + time
            </button>
          </div>
        </div>

        <label className="hb-field">
          <span>Grace window (min)</span>
          <input
            type="number"
            min={5}
            max={10080}
            value={draft.graceMinutes}
            onChange={(e) => setDraft((d) => ({ ...d, graceMinutes: Number(e.target.value) }))}
          />
          <small className="pl-help">How long past the time it still counts as done.</small>
        </label>

        <label className="hb-field pl-wide">
          <span>Note (optional)</span>
          <input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Beard, 1ml" />
        </label>
      </div>

      {err && <div className="hb-form__error">{err}</div>}
      <div className="pl-actions">
        <button className="ui-btn ui-btn--primary ui-btn--lg ui-btn--block" type="submit" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : 'Add routine'}
        </button>
      </div>
    </form>
  );
}

// --- Calendar --------------------------------------------------------------

function monthKey(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { month: 'long', ...(sameYear ? {} : { year: 'numeric' }) });
}

function CalendarTab({
  events,
  onChange,
  onAdd,
}: {
  events: EventOccurrence[];
  onChange: () => void;
  onAdd: () => void;
}): JSX.Element {
  const months = useMemo(() => {
    const out: { key: string; items: EventOccurrence[] }[] = [];
    for (const e of events) {
      const key = monthKey(e.startsAt);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(e);
      else out.push({ key, items: [e] });
    }
    return out;
  }, [events]);

  if (events.length === 0) {
    return (
      <div className="ui-group">
        <EmptyState
          icon="calendar"
          title="Nothing on the calendar"
          hint="Add vacations, birthdays, deadlines, renewals — anything you'd rather not have to remember. The coach starts prompting you when each one gets close."
          action={
            <Button icon="plus" onClick={onAdd}>
              New event
            </Button>
          }
        />
      </div>
    );
  }

  const remove = (e: EventOccurrence): void => {
    if (window.confirm(`Delete "${e.title}"?`)) {
      void deleteEvent(e.eventId).then(onChange);
    }
  };

  return (
    <div className="pl-stack">
      {months.map((m, i) => (
        <Group key={m.key} header={m.key} action={i === 0 ? { label: 'Add', onClick: onAdd } : undefined}>
          {m.items.map((e) => {
            const d = new Date(e.startsAt);
            const ic = eventIcon(e.kind);
            return (
              <Row
                key={`${e.eventId}:${e.startsAt}`}
                className="pl-event"
                leading={
                  <span className="pl-date" style={{ ['--ev' as string]: ic.color }} aria-hidden="true">
                    <span className="pl-date__dow">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                    <span className="pl-date__day num">{d.getDate()}</span>
                  </span>
                }
                title={e.title}
                sub={[e.kind !== 'event' ? e.kind.charAt(0).toUpperCase() + e.kind.slice(1) : null, e.location, e.detail].filter(Boolean).join(' · ') || undefined}
                detail={relativeDays(e.daysUntil)}
                swipe={{ trailing: [{ label: 'Delete', icon: 'trash', tone: 'bad', onAction: () => remove(e) }] }}
                trailing={<IconButton icon="trash" label={`Delete ${e.title}`} onClick={() => remove(e)} className="pl-del" />}
              />
            );
          })}
        </Group>
      ))}
    </div>
  );
}

const EVENT_KINDS = [
  'event',
  'vacation',
  'travel',
  'birthday',
  'anniversary',
  'appointment',
  'deadline',
  'bill',
  'renewal',
];

function EventForm({ onDone }: { onDone: () => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('event');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [location, setLocation] = useState('');
  const [detail, setDetail] = useState('');
  const [people, setPeople] = useState('');
  const [recurrence, setRecurrence] = useState<'none' | 'yearly' | 'monthly' | 'weekly' | 'daily'>(
    'none',
  );
  const [prep, setPrep] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A birthday is almost always annual; pre-set that so the common case is one
  // fewer decision, while still leaving it changeable.
  useEffect(() => {
    if (kind === 'birthday' || kind === 'anniversary') setRecurrence('yearly');
  }, [kind]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await createEvent({
        title: title.trim(),
        kind,
        // A date input gives YYYY-MM-DD; anchor it at local noon so a timezone
        // shift can never slide the event onto the previous day.
        startsAt: new Date(`${startsAt}T12:00:00`).toISOString(),
        endsAt: endsAt ? new Date(`${endsAt}T12:00:00`).toISOString() : null,
        location: location.trim() || null,
        detail: detail.trim() || null,
        people: people
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
        recurrence,
        ...(prep ? { prepHorizonDays: Number(prep) } : {}),
      });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not create event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hb-form pl-form" onSubmit={submit}>
      <div className="pl-grid">
        <label className="hb-field pl-wide">
          <span>What is it?</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Outer Banks trip"
            required
            autoFocus
          />
        </label>

        <label className="hb-field">
          <span>Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {EVENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>

        <label className="hb-field">
          <span>Repeats</span>
          <select
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value as typeof recurrence)}
          >
            <option value="none">Once</option>
            <option value="yearly">Every year</option>
            <option value="monthly">Every month</option>
            <option value="weekly">Every week</option>
            <option value="daily">Every day</option>
          </select>
        </label>

        <label className="hb-field">
          <span>Starts</span>
          <input
            type="date"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
        </label>

        <label className="hb-field">
          <span>Ends (optional)</span>
          <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>

        <label className="hb-field">
          <span>Where</span>
          <input value={location} onChange={(e) => setLocation(e.target.value)} />
        </label>

        <label className="hb-field">
          <span>Who (comma separated)</span>
          <input value={people} onChange={(e) => setPeople(e.target.value)} placeholder="Mom, Dave" />
        </label>

        <label className="hb-field">
          <span>Start prompting (days ahead)</span>
          <input
            type="number"
            min={0}
            max={365}
            value={prep}
            onChange={(e) => setPrep(e.target.value)}
            placeholder="auto"
          />
          <small className="pl-help">Blank picks a sensible default for the kind.</small>
        </label>

        <label className="hb-field pl-wide">
          <span>Notes</span>
          <input
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Anything the coach should know"
          />
        </label>
      </div>

      {err && <div className="hb-form__error">{err}</div>}
      <div className="pl-actions">
        <button className="ui-btn ui-btn--primary ui-btn--lg ui-btn--block" type="submit" disabled={busy || !title.trim() || !startsAt}>
          {busy ? 'Saving…' : 'Add event'}
        </button>
      </div>
    </form>
  );
}
